import Vue from 'vue';
import { defineStore } from 'pinia';
import http from '../api/httpRequest.js'
import useUserStore from './userStore.js'
import useFriendStore from './friendStore.js'
import { resolveGroupMemberDisplayName } from '../utils/identityDisplay.js'
import { normalizeTopMessages, removeTopMessages } from '../utils/topMessageState.js'
import { attachContactSearchText } from '../utils/contactSearchMatcher.js'
import { getDB } from '../db/index.js';
import { applyGroupMessageRemoval, isRemovedMemberMessage, mergeMessageRemovalCutoffs } from '../api/groupMessageCleanup.js';
import { applySyncPageState, createPageState, createSyncState, resetPageState } from './listPageState.js';

const MEMBER_DEFER_THRESHOLD = 120;
const MEMBER_APPLY_CHUNK_SIZE = 80;
const GROUP_MEMBER_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const GROUP_MEMBER_FIRST_PAGE_LIMIT = 30;
const GROUP_MEMBER_PAGE_LIMIT = 30;
const GROUP_MEMBER_BULK_PAGE_LIMIT = 500;
const GROUP_MEMBER_PAGE_LIMIT_MAX = 500;
const GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET = 500;
const GROUP_LIST_FULL_LOAD_PAGE_BUDGET = 500;
const GROUP_LIST_FIRST_PAGE_LIMIT = 30;
const GROUP_LIST_PAGE_LIMIT = 30;
const GROUP_LIST_PAGE_LIMIT_MAX = 30;
const GROUP_LIST_BACKGROUND_PAGE_BUDGET = 4;
let memberGroupIdsByUser = new Map();
let memberUserIdsByGroup = new Map();

function groupListSnapshotConfigKey() {
	return 'groupListSnapshotComplete';
}

function groupMembersSnapshotConfigKey(groupId) {
	return `groupMembersSnapshotComplete:${groupId}`;
}

function resolveIndexKey(value) {
	return value === undefined || value === null ? '' : String(value);
}

function resolveGroupStoreKey(value) {
	return resolveIndexKey(value);
}

function clearGroupMemberIndex(groupId) {
	const groupKey = resolveIndexKey(groupId);
	if (!groupKey) {
		return;
	}
	const userIds = memberUserIdsByGroup.get(groupKey);
	if (userIds) {
		userIds.forEach((userId) => {
			const groupIds = memberGroupIdsByUser.get(userId);
			if (!groupIds) {
				return;
			}
			groupIds.delete(groupId);
			if (groupIds.size === 0) {
				memberGroupIdsByUser.delete(userId);
			}
		});
	}
	memberUserIdsByGroup.delete(groupKey);
}

function indexGroupMember(groupId, member) {
	const groupKey = resolveIndexKey(groupId);
	const userKey = resolveIndexKey(member && member.userId);
	if (!groupKey || !userKey) {
		return;
	}
	let userIds = memberUserIdsByGroup.get(groupKey);
	if (!userIds) {
		userIds = new Set();
		memberUserIdsByGroup.set(groupKey, userIds);
	}
	userIds.add(userKey);
	let groupIds = memberGroupIdsByUser.get(userKey);
	if (!groupIds) {
		groupIds = new Set();
		memberGroupIdsByUser.set(userKey, groupIds);
	}
	groupIds.add(groupId);
}

function resetMemberGroupIndex() {
	memberGroupIdsByUser = new Map();
	memberUserIdsByGroup = new Map();
}

function createMemberLookup(members = []) {
	const lookup = Object.create(null);
	if (!Array.isArray(members)) {
		return lookup;
	}
	members.forEach((member) => {
		if (member && member.userId != null) {
			lookup[String(member.userId)] = member;
		}
	});
	return lookup;
}

function setGroupMemberLookup(group, lookup) {
	if (!group) {
		return;
	}
	clearGroupMemberIndex(group.id);
	Object.defineProperty(group, '_memberLookup', {
		value: lookup || Object.create(null),
		enumerable: false,
		writable: true,
		configurable: true
	});
	Object.keys(group._memberLookup).forEach((userId) => {
		indexGroupMember(group.id, group._memberLookup[userId]);
	});
}

function ensureGroupMemberLookup(group) {
	if (!group) {
		return Object.create(null);
	}
	if (!group._memberLookup) {
		setGroupMemberLookup(group, createMemberLookup(group.members));
	}
	return group._memberLookup;
}

function upsertGroupMemberLookup(group, member) {
	if (!group || !member || member.userId == null) {
		return;
	}
	ensureGroupMemberLookup(group)[String(member.userId)] = member;
	indexGroupMember(group.id, member);
}

function setReactiveIfMissing(target, key, value) {
	if (!target || Object.prototype.hasOwnProperty.call(target, key)) {
		return;
	}
	Vue.set(target, key, value);
}

function ensureMemberRuntimeState(group) {
	if (!group) {
		return;
	}
	if (!group.memberPage || typeof group.memberPage !== 'object') {
		Vue.set(group, 'memberPage', {});
	}
	if (!group.memberSync || typeof group.memberSync !== 'object') {
		Vue.set(group, 'memberSync', {});
	}
	if (!group.memberTasks || typeof group.memberTasks !== 'object') {
		Vue.set(group, 'memberTasks', {});
	}
	const total = Math.max(0, Math.floor(toNumber(group.memberPage.total, group.memberCount)));
	setReactiveIfMissing(group.memberPage, 'loading', false);
	setReactiveIfMissing(group.memberPage, 'loaded', false);
	setReactiveIfMissing(group.memberPage, 'error', false);
	setReactiveIfMissing(group.memberPage, 'loadedAt', 0);
	setReactiveIfMissing(group.memberPage, 'more', false);
	setReactiveIfMissing(group.memberPage, 'total', total);
	setReactiveIfMissing(group.memberPage, 'cursor', '');
	setReactiveIfMissing(group.memberPage, 'loadingMore', false);
	setReactiveIfMissing(group.memberPage, 'moreError', false);
	setReactiveIfMissing(group.memberSync, 'rev', 0);
	setReactiveIfMissing(group.memberSync, 'base', 0);
	setReactiveIfMissing(group.memberTasks, 'refresh', null);
	setReactiveIfMissing(group.memberTasks, 'mode', '');
	setReactiveIfMissing(group.memberTasks, 'more', null);
}

function scheduleIdleTask(task, timeout = 160) {
	if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
		window.requestIdleCallback(task, { timeout });
		return;
	}
	setTimeout(task, 0);
}

function applyInIdleChunks(items, handleChunk, chunkSize = MEMBER_APPLY_CHUNK_SIZE) {
	if (!Array.isArray(items) || items.length === 0) {
		return Promise.resolve();
	}
	return new Promise((resolve) => {
		let index = 0;
		const step = () => {
			const chunk = items.slice(index, index + chunkSize);
			index += chunk.length;
			handleChunk(chunk);
			if (index >= items.length) {
				resolve();
				return;
			}
			scheduleIdleTask(step);
		};
		scheduleIdleTask(step);
	});
}

function toNumber(value, fallback = 0) {
	const num = Number(value);
	return Number.isFinite(num) ? num : fallback;
}

function toIntVersion(value) {
	const normalized = Math.floor(toNumber(value, 0));
	return normalized > 0 ? normalized : 0;
}

function normalizeGroupListPagePayload(payload) {
	const normalized = payload && typeof payload === 'object' ? payload : {};
	const items = Array.isArray(normalized.items) ? normalized.items : [];
	const total = Math.floor(toNumber(normalized.total, 0));
	return {
		items,
		more: Boolean(normalized.more),
		total: total > 0 ? total : 0,
		cursor: normalized.cursor ? String(normalized.cursor) : '',
		rev: toIntVersion(normalized.rev),
		reset: normalized.reset === true,
		reason: normalized.reason ? String(normalized.reason) : ''
	};
}

function normalizeGroupMemberPagePayload(payload) {
	const normalized = payload && typeof payload === 'object' ? payload : {};
	const items = Array.isArray(normalized.items) ? normalized.items : [];
	const total = Math.floor(toNumber(normalized.total, 0));
	return {
		items,
		more: Boolean(normalized.more),
		total: total > 0 ? total : 0,
		cursor: normalized.cursor ? String(normalized.cursor) : '',
		rev: toIntVersion(normalized.rev),
		reset: normalized.reset === true,
		reason: normalized.reason ? String(normalized.reason) : ''
	};
}

function sanitizeGroupForPersist(group) {
	if (!group || group.id === undefined || group.id === null) {
		return null;
	}
	const snapshot = { ...group };
	delete snapshot.members;
	delete snapshot._memberLookup;
	delete snapshot.memberPage;
	delete snapshot.memberSync;
	delete snapshot.memberTasks;
	delete snapshot.onlineCount;
	delete snapshot.rtcInfo;
	return snapshot;
}

function normalizeGroupMemberForRuntime(member) {
	if (!member || typeof member !== 'object') {
		return member;
	}
	delete member.checked;
	delete member.locked;
	delete member.hide;
	member.version = toNumber(member.version);
	attachContactSearchText(member);
	return member;
}

function dedupeGroupsById(groups = []) {
	const result = [];
	const seen = new Set();
	(Array.isArray(groups) ? groups : []).forEach((group) => {
		const groupKey = resolveGroupStoreKey(group && group.id);
		if (!groupKey || seen.has(groupKey)) {
			return;
		}
		seen.add(groupKey);
		result.push(group);
	});
	return result;
}

export default defineStore('groupStore', {
	state: () => {
		return {
			groups: [],// 数组用于展示，防止更新时不刷新
			groupMap: new Map(), // map用于查询，提高查询效率
			groupInfoTasks: new Map(),
			groupInfoRequestEpoch: 0,
			groupPersistTimerMap: new Map(),
			list: {
				page: createPageState({
					loaded: false
				}),
				sync: createSyncState(),
				tasks: {
					bg: null,
					load: null,
					full: null,
					localReady: null
				},
				rebuild: {
					active: false,
					rows: []
				}
			}
		}
	},
	actions: {
		resetGroupListState(options = {}) {
			const clearTasks = options.clearTasks !== false;
			this.list.sync.rev = 0;
			this.list.sync.base = 0;
			resetPageState(this.list.page);
			this.list.page.loaded = false;
			if (clearTasks) {
				this.list.tasks.bg = null;
				this.list.tasks.full = null;
			}
			this.resetGroupListRebuildState();
		},
		setGroupListPageState(page, requestVersion) {
			applySyncPageState(this.list.page, this.list.sync, page, requestVersion, toIntVersion);
			this.list.page.loaded = true;
		},
		groupSnapshotRowCount(groups = this.groups) {
			return dedupeGroupsById(groups).filter(group => group && group.id !== undefined && group.id !== null).length;
		},
		hasCompleteGroupSnapshot() {
			const total = Math.max(0, Math.floor(Number(this.list.page.total || 0)));
			return this.list.page.loaded && total >= 0 && !this.list.page.more && this.groupSnapshotRowCount() >= total;
		},
		hasIncompleteGroupSnapshot() {
			const total = Math.max(0, Math.floor(Number(this.list.page.total || 0)));
			return total > 0 && !this.list.page.more && this.groupSnapshotRowCount() < total;
		},
		resetGroupListRebuildState() {
			this.list.rebuild.active = false;
			this.list.rebuild.rows = [];
		},
		beginGroupListRebuild() {
			this.list.rebuild.active = true;
			this.list.rebuild.rows = [];
		},
		appendGroupListRebuildRows(items = []) {
			if (!this.list.rebuild.active) {
				this.beginGroupListRebuild();
			}
			this.list.rebuild.rows = dedupeGroupsById(this.list.rebuild.rows.concat(Array.isArray(items) ? items : []));
			return this.list.rebuild.rows;
		},
		normalizeGroupListSnapshotMeta(meta) {
			if (!meta || meta.complete !== true) {
				return null;
			}
			return {
				complete: true,
				total: Math.max(0, Math.floor(toNumber(meta.total, 0))),
				rev: toIntVersion(meta.rev),
				snapshotAt: Math.max(0, Math.floor(toNumber(meta.snapshotAt, 0)))
			};
		},
		isGroupListSnapshotMetaUsable(meta, localGroups = this.groups) {
			const snapshotMeta = this.normalizeGroupListSnapshotMeta(meta);
			if (!snapshotMeta) {
				return false;
			}
			return this.groupSnapshotRowCount(localGroups) >= snapshotMeta.total;
		},
		async findGroupListSnapshotMeta() {
			try {
				return this.normalizeGroupListSnapshotMeta(await getDB().findConfig(groupListSnapshotConfigKey()));
			} catch (error) {
				console.warn('读取群列表本地快照元数据失败', error);
				return null;
			}
		},
		async deleteGroupListSnapshotMeta() {
			try {
				await getDB().deleteConfig(groupListSnapshotConfigKey());
			} catch (error) {
				console.warn('清理群列表本地快照元数据失败', error);
			}
		},
		async persistGroupListSnapshotMeta(groups = this.groups, options = {}) {
			const total = Math.max(
				0,
				Math.floor(toNumber(options.total, this.groupSnapshotRowCount(groups)))
			);
			const rev = toIntVersion(options.rev !== undefined ? options.rev : this.maxGroupVersion(groups));
			try {
				await getDB().saveConfig(groupListSnapshotConfigKey(), {
					complete: true,
					total,
					rev,
					snapshotAt: Date.now()
				});
			} catch (error) {
				console.warn('保存群列表本地快照元数据失败', error);
			}
		},
		ensureGroupState(group) {
			if (!group) {
				return null;
			}
			setReactiveIfMissing(group, 'members', []);
			setReactiveIfMissing(group, 'rtcInfo', {});
			setReactiveIfMissing(group, 'topMessages', []);
			setReactiveIfMissing(group, 'memberCount', 0);
			setReactiveIfMissing(group, 'onlineCount', 0);
			ensureMemberRuntimeState(group);
			ensureGroupMemberLookup(group);
			return group;
		},
		resetGroupMemberPagingState(group) {
			if (!group) {
				return;
			}
			ensureMemberRuntimeState(group);
			group.memberSync.rev = 0;
			group.memberSync.base = 0;
			resetPageState(group.memberPage, {
				loaded: group.memberPage.loaded,
				loadedAt: group.memberPage.loadedAt,
				loadingMore: false,
				moreError: false
			});
			group.memberTasks.more = null;
		},
		setGroupMemberPageState(group, page, requestVersion) {
			if (!group) {
				return;
			}
			ensureMemberRuntimeState(group);
			applySyncPageState(group.memberPage, group.memberSync, page, requestVersion, toIntVersion);
			group.memberPage.moreError = false;
		},
		resolveExpectedMemberCount(group, options = {}) {
			const expectedCount = Number(options.expectedCount);
			if (Number.isFinite(expectedCount) && expectedCount >= 0) {
				return expectedCount;
			}
			const groupMemberCount = Number(group && group.memberCount);
			if (Number.isFinite(groupMemberCount) && groupMemberCount >= 0) {
				return groupMemberCount;
			}
			return 0;
		},
		shouldInvalidateMemberSnapshot(group, nextMemberCount) {
			if (!group || !group.memberPage.loaded) {
				return false;
			}
			if (group.memberPage.more) {
				return false;
			}
			if (!Number.isFinite(nextMemberCount) || nextMemberCount < 0) {
				return false;
			}
			return this.countActiveMembers(group.members) !== nextMemberCount;
		},
		initGroups(groups) {
			this.groups = [];
			this.groupMap.clear();
			resetMemberGroupIndex();
			this.setGroups(groups);
		},
		replaceGroupsSnapshot(groups) {
			const groupList = Array.isArray(groups) ? groups : [];
			const existingGroups = new Map(this.groups.map(group => [resolveGroupStoreKey(group && group.id), group]));
			const oldGroupKeys = new Set(existingGroups.keys());
			const nextGroupKeys = new Set();
			this.groups = [];
			this.groupMap.clear();
			resetMemberGroupIndex();
			groupList.forEach((group) => {
				const groupKey = resolveGroupStoreKey(group && group.id);
				nextGroupKeys.add(groupKey);
				const oldGroup = existingGroups.get(groupKey);
				if (oldGroup && Array.isArray(oldGroup.members) && oldGroup.members.length > 0) {
					ensureMemberRuntimeState(oldGroup);
					ensureMemberRuntimeState(group);
					group.members = oldGroup.members;
					group.memberPage.loaded = oldGroup.memberPage.loaded;
					group.memberPage.error = oldGroup.memberPage.error;
					group.memberPage.loadedAt = oldGroup.memberPage.loadedAt;
					group.memberSync.rev = oldGroup.memberSync.rev || 0;
					group.memberSync.base = oldGroup.memberSync.base || 0;
					group.memberPage.more = Boolean(oldGroup.memberPage.more && oldGroup.memberPage.cursor);
					group.memberPage.total = Math.max(0, Math.floor(toNumber(oldGroup.memberPage.total, group.memberCount)));
					group.memberPage.cursor = oldGroup.memberPage.more && oldGroup.memberPage.cursor
						? String(oldGroup.memberPage.cursor)
						: '';
					group.memberPage.loadingMore = oldGroup.memberPage.loadingMore || false;
					group.memberPage.moreError = oldGroup.memberPage.moreError || false;
					group.memberTasks.refresh = oldGroup.memberTasks.refresh || null;
					group.memberTasks.mode = oldGroup.memberTasks.mode || '';
					group.memberTasks.more = oldGroup.memberTasks.more || null;
					group.onlineCount = oldGroup.onlineCount || 0;
				}
				this.addGroup(group, {
					prepend: false
				});
			});
			oldGroupKeys.forEach((groupKey) => {
				if (!nextGroupKeys.has(groupKey)) {
					const oldGroup = existingGroups.get(groupKey);
					getDB().deleteGroupMembers(oldGroup && oldGroup.id !== undefined ? oldGroup.id : groupKey).catch((error) => {
						console.warn('清理已失效群成员缓存失败', error);
					});
				}
			});
		},
		setGroups(groups) {
			// 逐个群处理，保证ws重连后群成员不被清理
			const groupList = Array.isArray(groups) ? groups : [];
			groupList.forEach(group => this.addGroup(group));
		},
		async persistGroup(group) {
			const snapshot = sanitizeGroupForPersist(group);
			if (!snapshot) {
				return;
			}
			try {
				await getDB().saveGroup(snapshot);
			} catch (error) {
				console.warn('保存群本地快照失败', error);
			}
		},
		schedulePersistGroup(group, delayMs = 300) {
			const snapshot = sanitizeGroupForPersist(group);
			if (!snapshot) {
				return;
			}
			const groupKey = resolveGroupStoreKey(snapshot.id);
			const oldTimer = this.groupPersistTimerMap.get(groupKey);
			if (oldTimer) {
				clearTimeout(oldTimer);
			}
			const timer = setTimeout(() => {
				this.groupPersistTimerMap.delete(groupKey);
				this.persistGroup(snapshot);
			}, delayMs);
			this.groupPersistTimerMap.set(groupKey, timer);
		},
		async persistGroups(groups) {
			const snapshots = (Array.isArray(groups) ? groups : [])
				.map(sanitizeGroupForPersist)
				.filter(Boolean);
			if (snapshots.length === 0) {
				return;
			}
			try {
				await getDB().saveGroups(snapshots);
			} catch (error) {
				console.warn('保存群本地分页快照失败', error);
			}
		},
		async persistGroupSnapshot(groups = this.groups) {
			const snapshots = (Array.isArray(groups) ? groups : [])
				.map(sanitizeGroupForPersist)
				.filter(Boolean);
			try {
				await getDB().syncAllGroups(snapshots);
				await this.persistGroupListSnapshotMeta(snapshots, {
					total: this.list.page.total || snapshots.length,
					rev: this.list.sync.rev || this.maxGroupVersion(snapshots)
				});
			} catch (error) {
				console.warn('同步群本地完整快照失败', error);
			}
		},
		addGroup(group, options = {}) {
			const groupKey = resolveGroupStoreKey(group && group.id);
			if (!groupKey) {
				return;
			}
			const prepend = options.prepend !== false;
			if (this.groupMap.has(groupKey)) {
				this.updateGroup(group)
			} else {
				ensureMemberRuntimeState(group);
				group.members = Array.isArray(group.members) ? group.members : [];
				group.rtcInfo = {};
				group.topMessages = normalizeTopMessages(group.topMessages);
				group.memberCount = Number(group.memberCount) || 0;
				group.onlineCount = group.onlineCount ?? 0;
				group.memberPage.loading = false;
				group.memberPage.loaded = Boolean(group.memberPage.loaded || group.members.length > 0);
				group.memberPage.error = false;
				group.memberPage.loadedAt = group.memberPage.loadedAt || (group.members.length > 0 ? Date.now() : 0);
				group.memberSync.rev = toIntVersion(group.memberSync.rev);
				group.memberSync.base = toIntVersion(group.memberSync.base);
				group.memberPage.more = Boolean(group.memberPage.more && group.memberPage.cursor);
				group.memberPage.total = Math.max(0, Math.floor(toNumber(group.memberPage.total, group.memberCount)));
				group.memberPage.cursor = group.memberPage.more && group.memberPage.cursor
					? String(group.memberPage.cursor)
					: '';
				group.memberPage.loadingMore = false;
				group.memberPage.moreError = false;
				group.memberTasks.refresh = null;
				group.memberTasks.mode = '';
				group.memberTasks.more = null;
				group.members.forEach(member => this.normalizeGroupMember(member));
				setGroupMemberLookup(group, createMemberLookup(group.members));
				// 初始化群组权限字段(默认值)
				if (group.isAllowViewMemberProfile === undefined) {
					group.isAllowViewMemberProfile = true;
				}
				if (group.isAllowViewOnlineCount === undefined) {
					group.isAllowViewOnlineCount = true;
				}
				if (group.isAllowViewHistory === undefined) {
					group.isAllowViewHistory = true;
				}
				if (prepend) {
					this.groups.unshift(group);
				} else {
					this.groups.push(group);
				}
				this.groupMap.set(groupKey, group);
				this.ensureGroupState(group);
			}
		},
		removeGroup(id) {
			let group = this.findGroup(id);
			if (!group) {
				return;
			}
			group.quit = true;
			clearGroupMemberIndex(id);
		},
		updateGroup(group) {
			if (!group || group.id === undefined || group.id === null || group.id === '') {
				return false;
			}
			let g = this.findGroup(group.id);
			if (!g) {
				console.warn('[groupStore] updateGroup skipped because group not found', group.id);
				return false;
			}
			this.ensureGroupState(g);
			// 成员列表保持不变
			group.members = g.members;
			// 部分群资料接口不会返回 topMessages，这里保留本地已同步的置顶状态，避免被空数据覆盖。
			group.topMessages = Array.isArray(group.topMessages)
				? normalizeTopMessages(group.topMessages)
				: normalizeTopMessages(g.topMessages);
			const nextMemberCount = Number.isFinite(Number(group.memberCount))
				? Number(group.memberCount)
				: (g.memberCount ?? this.countActiveMembers(g.members));
			ensureMemberRuntimeState(group);
			ensureMemberRuntimeState(g);
			group.memberCount = nextMemberCount;
			group.onlineCount = g.onlineCount ?? 0;
				group.memberPage.loading = g.memberPage.loading;
				group.memberPage.loaded = this.shouldInvalidateMemberSnapshot(g, nextMemberCount)
					? false
					: g.memberPage.loaded;
				group.memberPage.error = g.memberPage.error;
				group.memberPage.loadedAt = g.memberPage.loadedAt || 0;
				group.memberSync.rev = g.memberSync.rev || 0;
				group.memberSync.base = g.memberSync.base || 0;
				group.memberPage.more = Boolean(g.memberPage.more && g.memberPage.cursor);
				group.memberPage.total = Math.max(0, Math.floor(toNumber(g.memberPage.total, nextMemberCount)));
				group.memberPage.cursor = g.memberPage.more && g.memberPage.cursor
					? String(g.memberPage.cursor)
					: '';
				group.memberPage.loadingMore = g.memberPage.loadingMore || false;
				group.memberPage.moreError = g.memberPage.moreError || false;
				group.memberTasks.refresh = g.memberTasks.refresh || null;
				group.memberTasks.mode = g.memberTasks.mode || '';
				group.memberTasks.more = g.memberTasks.more || null;
				// 拷贝属性
				Object.assign(g, group);
				if (!g.memberPage.loaded || this.shouldInvalidateMemberSnapshot(g, nextMemberCount)) {
					this.resetGroupMemberPagingState(g);
				}
				const userStore = useUserStore();
				this.syncCurrentGroupMemberDisplay(g.id, userStore.userInfo && userStore.userInfo.id, g);
			this.schedulePersistGroup(g);
			return true;
		},
		upsertGroup(group, options = {}) {
			if (!group || group.id === undefined || group.id === null || group.id === '') {
				return false;
			}
			if (this.findGroup(group.id)) {
				return this.updateGroup(group);
			}
			this.addGroup(group, {
				prepend: options.prepend === true
			});
			const insertedGroup = this.findGroup(group.id);
			if (insertedGroup) {
				this.schedulePersistGroup(insertedGroup);
			}
			return Boolean(insertedGroup);
		},
		fetchGroupInfo(id) {
			if (id === undefined || id === null || id === '') {
				return Promise.resolve(null);
			}
			const groupKey = resolveGroupStoreKey(id);
			const pendingTask = this.groupInfoTasks.get(groupKey);
			if (pendingTask) {
				return pendingTask;
			}
			const requestTask = http({
				url: `/group/find/${id}`,
				method: 'get',
				_skipErrorMessage: true
			});
			const trackedTask = requestTask.finally(() => {
				if (this.groupInfoTasks.get(groupKey) === trackedTask) {
					this.groupInfoTasks.delete(groupKey);
				}
			});
			this.groupInfoTasks.set(groupKey, trackedTask);
			return trackedTask;
		},
		async loadGroupInfo(id) {
			if (id === undefined || id === null || id === '') {
				return null;
			}
			const requestEpoch = this.groupInfoRequestEpoch;
			const group = await this.fetchGroupInfo(id);
			if (requestEpoch !== this.groupInfoRequestEpoch) {
				return null;
			}
			if (!group || group.id === undefined || group.id === null || group.id === '') {
				return null;
			}
			this.upsertGroup(group);
			return this.findGroup(group.id);
		},
		normalizeGroupMember(member) {
			const normalizedMember = normalizeGroupMemberForRuntime(member);
			if (normalizedMember) {
				this.applyGroupMemberIdentity(normalizedMember);
			}
			return normalizedMember;
		},
		applyGroupMemberIdentity(member) {
			if (!member || !member.userId) {
				return member;
			}
			const friendStore = useFriendStore();
			const userStore = useUserStore();
			const friend = friendStore.findFriend(member.userId);
			const activeFriend = friend && !friend.deleted ? friend : null;
			const user = userStore.userInfo && userStore.userInfo.id == member.userId
				? userStore.userInfo
				: null;
			const nextUserName = (activeFriend && activeFriend.userName) || (user && user.userName);
			if ((!member.userName || String(member.userName).trim() === '') && nextUserName) {
				member.userName = nextUserName;
			}
			const nextShowName = resolveGroupMemberDisplayName(member, activeFriend, user);
			if (nextShowName && member.showNickName !== nextShowName) {
				member.showNickName = nextShowName;
			}
			// 群成员头像以群成员快照为准，避免被好友侧历史快照覆盖。
			const selfHeadImage = user && (user.headImageThumb || user.headImage);
			if ((!member.headImage || String(member.headImage).trim() === '') && selfHeadImage) {
				member.headImage = selfHeadImage;
			}
			attachContactSearchText(member);
			return member;
		},
		syncGroupMemberFromFriend(friend) {
			if (!friend || !friend.id) {
				return;
			}
			const groupIds = memberGroupIdsByUser.get(String(friend.id));
			if (!groupIds || groupIds.size === 0) {
				return;
			}
			Array.from(groupIds).forEach((groupId) => {
				const member = this.findGroupMember(groupId, friend.id);
				if (member) {
					this.applyGroupMemberIdentity(member);
				}
			});
		},
		syncGroupMemberFromUser(user) {
			if (!user || !user.id) {
				return [];
			}
			const groupIds = memberGroupIdsByUser.get(String(user.id));
			if (!groupIds || groupIds.size === 0) {
				return [];
			}
			const changedGroupIds = [];
			Array.from(groupIds).forEach((groupId) => {
				const member = this.findGroupMember(groupId, user.id);
				if (member) {
					const before = {
						headImage: member.headImage,
						userNickName: member.userNickName,
						userName: member.userName,
						showNickName: member.showNickName
					};
					if (Object.prototype.hasOwnProperty.call(user, 'headImageThumb')
						|| Object.prototype.hasOwnProperty.call(user, 'headImage')) {
						const nextHeadImage = user.headImageThumb || user.headImage || '';
						member.headImage = nextHeadImage;
					}
					if (Object.prototype.hasOwnProperty.call(user, 'nickName')) {
						member.userNickName = user.nickName || '';
					}
					if (Object.prototype.hasOwnProperty.call(user, 'userName')) {
						member.userName = user.userName || '';
					}
					this.applyGroupMemberIdentity(member);
					const changed = member.headImage !== before.headImage
						|| member.userNickName !== before.userNickName
						|| member.userName !== before.userName
						|| member.showNickName !== before.showNickName;
					if (changed) {
						changedGroupIds.push(groupId);
						this.persistGroupMembers(this.findGroup(groupId), {
							incomingMembers: [member],
							incrementalOnly: true
						}).catch(() => []);
					}
				}
			});
			return changedGroupIds;
		},
		applyGroupMemberCount(groupId, memberCount) {
			const group = this.findGroup(groupId);
			const rawCount = Number(memberCount);
			if (!group || !Number.isFinite(rawCount) || rawCount < 0) {
				return false;
			}
			const nextCount = Math.max(0, Math.floor(rawCount));
			let changed = false;
			if (group.memberCount !== nextCount) {
				group.memberCount = nextCount;
				changed = true;
			}
			this.ensureGroupState(group);
			if (group.memberPage && group.memberPage.total !== nextCount) {
				group.memberPage.total = nextCount;
				changed = true;
			}
			if (changed) {
				this.schedulePersistGroup(group);
			}
			return changed;
		},
		syncCurrentGroupMemberDisplay(groupId, userId, groupProfile) {
			if (!groupId || !userId || !groupProfile) {
				return;
			}
			const member = this.findGroupMember(groupId, userId);
			if (!member) {
				return;
			}
			member.remarkNickName = groupProfile.remarkNickName;
			const userStore = useUserStore();
			const user = userStore.userInfo && userStore.userInfo.id == userId
				? userStore.userInfo
				: null;
			const nextShowName = resolveGroupMemberDisplayName({
				...member,
				remarkNickName: groupProfile.remarkNickName,
				showNickName: groupProfile.showNickName
			}, null, user);
			if (nextShowName) {
				member.showNickName = nextShowName;
			}
			attachContactSearchText(member);
		},
		countActiveMembers(members) {
			return Array.isArray(members) ? members.filter(member => !member.quit).length : 0;
		},
		hasCompleteMemberSnapshot(id) {
			const group = this.findGroup(id);
			if (!group) {
				return false;
			}
			this.ensureGroupState(group);
			const total = Math.max(0, Math.floor(Number(group.memberPage.total || group.memberCount || 0)));
			if (total <= 0) {
				return Boolean(group.memberPage.loaded && !group.memberPage.more);
			}
			return Boolean(group.memberPage.loaded && !group.memberPage.more && this.countActiveMembers(group.members) >= total);
		},
		countOnlineMembers(members) {
			return Array.isArray(members) ? members.filter(member => member && !member.quit && member.online).length : 0;
		},
		maxGroupMemberVersion(members) {
			return Math.max(0, ...(Array.isArray(members) ? members : []).map(member => toNumber(member && member.version)));
		},
		maxGroupVersion(groups = this.groups) {
			const list = Array.isArray(groups) ? groups : [];
			return Math.max(0, ...list.map(group => toIntVersion(group && group.version)));
		},
		buildGroupListRequestVersion(localGroups = this.groups, snapshotMeta = null) {
			if (!this.isGroupListSnapshotMetaUsable(snapshotMeta, localGroups)) {
				return 0;
			}
			const metaRev = toIntVersion(snapshotMeta && snapshotMeta.rev);
			return metaRev > 0 ? metaRev : this.maxGroupVersion(localGroups);
		},
		fetchGroupListPage({ rev = 0, cursor = '', limit = GROUP_LIST_PAGE_LIMIT } = {}) {
			const requestRev = toIntVersion(rev);
			const safeLimit = Math.max(1, Math.min(GROUP_LIST_PAGE_LIMIT_MAX,
				toNumber(limit, GROUP_LIST_PAGE_LIMIT) || GROUP_LIST_PAGE_LIMIT));
			const params = new URLSearchParams();
			params.set('rev', String(requestRev));
			params.set('limit', String(safeLimit));
			if (cursor) {
				params.set('cursor', String(cursor));
			}
			return http({
				url: `/group/list?${params.toString()}`,
				method: 'GET'
			}).then(payload => normalizeGroupListPagePayload(payload));
		},
		fetchGroupMemberPage(groupId, { rev = 0, cursor = '', limit = GROUP_MEMBER_PAGE_LIMIT, keyword = '' } = {}) {
			const requestRev = toIntVersion(rev);
			const safeLimit = Math.max(1, Math.min(
				GROUP_MEMBER_PAGE_LIMIT_MAX,
				toNumber(limit, GROUP_MEMBER_PAGE_LIMIT) || GROUP_MEMBER_PAGE_LIMIT
			));
			const safeKeyword = String(keyword || '').trim();
			const params = new URLSearchParams();
			params.set('rev', String(requestRev));
			params.set('limit', String(safeLimit));
			if (cursor) {
				params.set('cursor', String(cursor));
			}
			if (safeKeyword) {
				params.set('keyword', safeKeyword);
			}
			return http({
				url: `/group/members/${groupId}?${params.toString()}`,
				method: 'get'
			}).then(payload => normalizeGroupMemberPagePayload(payload));
		},
			async fetchGroupMemberSyncPage(groupId, rev = 0, options = {}) {
				const requestRev = toIntVersion(rev);
				const pageLimit = Math.max(1, Math.min(
					GROUP_MEMBER_PAGE_LIMIT_MAX,
					toNumber(options.limit, GROUP_MEMBER_PAGE_LIMIT) || GROUP_MEMBER_PAGE_LIMIT
				));
				const pageBudget = Math.max(1, Math.floor(toNumber(options.maxPages, 1) || 1));
				let cursor = options.cursor ? String(options.cursor) : '';
				let mergedItems = [];
				let more = false;
				let next = '';
				let revHead = requestRev;
				let total = 0;
				let fetchedPages = 0;
				while (fetchedPages < pageBudget) {
					const page = await this.fetchGroupMemberPage(groupId, {
						rev: requestRev,
						cursor,
						limit: pageLimit,
						keyword: options.keyword
					});
					if (page.reset === true && requestRev > 0) {
						return this.fetchGroupMemberSyncPage(groupId, 0, {
							...options,
							cursor: '',
							maxPages: pageBudget
						});
					}
					const items = Array.isArray(page.items) ? page.items : [];
					if (items.length > 0) {
						mergedItems = mergedItems.concat(items);
					}
					more = Boolean(page.more && page.cursor);
					next = more ? String(page.cursor) : '';
					revHead = toIntVersion(page.rev);
					total = Math.max(total, Math.floor(toNumber(page.total, 0)));
					fetchedPages += 1;
					if (!more || !next || fetchedPages >= pageBudget) {
						break;
					}
					cursor = next;
				}
				return {
					items: mergedItems,
					more,
					total,
					cursor: next,
					rev: revHead,
					requestRev
				};
			},
			syncGroupMembers(groupId, rev = 0, options = {}) {
				const pageBudget = Math.max(1, Math.floor(toNumber(options.maxPages, 1) || 1));
				return this.fetchGroupMemberSyncPage(groupId, rev, {
					...options,
					maxPages: pageBudget
				}).then(page => page.items);
			},
		mergeGroups(items = []) {
			(Array.isArray(items) ? items : []).forEach((group) => {
				if (!group || group.id === undefined || group.id === null || group.id === '') {
					return;
				}
				this.addGroup(group);
				if (group.quit === true || group.dissolve === true) {
					clearGroupMemberIndex(group.id);
				}
			});
		},
		async pullGroupListPage(options = {}) {
			const requestRev = toIntVersion(options.rev !== undefined
				? options.rev
				: this.list.sync.rev);
			const requestCursor = options.cursor || '';
			const fullSnapshot = requestRev === 0;
			if (fullSnapshot && !requestCursor) {
				this.beginGroupListRebuild();
				await this.deleteGroupListSnapshotMeta();
			}
			const page = await this.fetchGroupListPage({
				rev: requestRev,
				cursor: requestCursor,
				limit: options.limit || GROUP_LIST_PAGE_LIMIT
			});
			if (page.reset === true && requestRev > 0) {
				await getDB().syncAllGroups([]);
				await this.deleteGroupListSnapshotMeta();
				this.initGroups([]);
				this.resetGroupListState();
				return this.pullGroupListPage({
					...options,
					rev: 0,
					cursor: ''
				});
			}
			const items = Array.isArray(page.items) ? page.items : [];
			if (fullSnapshot) {
				const snapshotRows = this.appendGroupListRebuildRows(items);
				this.replaceGroupsSnapshot(snapshotRows);
			} else {
				this.mergeGroups(items);
			}
			this.setGroupListPageState(page, requestRev);
			if (fullSnapshot && !page.more) {
				await this.persistGroupSnapshot(this.groups);
				this.resetGroupListRebuildState();
			} else {
				await this.persistGroups(items);
				if (!page.more && this.hasCompleteGroupSnapshot()) {
					await this.persistGroupListSnapshotMeta(this.groups, {
						total: page.total,
						rev: page.rev
					});
				}
			}
			return page;
		},
		async loadMoreGroupsFromCursor(options = {}) {
			if (this.list.tasks.bg) {
				return this.list.tasks.bg;
			}
			if (this.list.page.loading || !this.list.page.more || !this.list.page.cursor) {
				return Promise.resolve({
					items: [],
					more: this.list.page.more
				});
			}
			const task = (async () => {
				const requestRev = toIntVersion(options.rev !== undefined
					? options.rev
					: this.list.sync.base);
				const pageBudget = Math.max(1, Math.floor(toNumber(options.maxPages,
					GROUP_LIST_BACKGROUND_PAGE_BUDGET) || GROUP_LIST_BACKGROUND_PAGE_BUDGET));
				const pageLimit = Math.max(1, Math.min(GROUP_LIST_PAGE_LIMIT_MAX,
					toNumber(options.limit, GROUP_LIST_PAGE_LIMIT) || GROUP_LIST_PAGE_LIMIT));
				let cursor = this.list.page.cursor;
				let more = this.list.page.more;
				let fetchedPages = 0;
				let mergedItems = [];
				this.list.page.loading = true;
				this.list.page.error = false;
				try {
					while (more && cursor && fetchedPages < pageBudget) {
						const page = await this.fetchGroupListPage({
							rev: requestRev,
							cursor,
							limit: pageLimit
						});
						if (page.reset === true && requestRev > 0) {
							await getDB().syncAllGroups([]);
							await this.deleteGroupListSnapshotMeta();
							this.initGroups([]);
							this.resetGroupListState();
							this.list.page.loading = false;
							await this.pullGroupListPage({
								rev: 0,
								cursor: '',
								limit: GROUP_LIST_FIRST_PAGE_LIMIT
							});
							break;
						}
						const items = Array.isArray(page.items) ? page.items : [];
						if (requestRev === 0) {
							const snapshotRows = this.appendGroupListRebuildRows(items);
							this.replaceGroupsSnapshot(snapshotRows);
						} else {
							this.mergeGroups(items);
						}
						mergedItems = mergedItems.concat(items);
						this.setGroupListPageState(page, requestRev);
						if (!this.list.page.more && requestRev === 0) {
							await this.persistGroupSnapshot(this.groups);
							this.resetGroupListRebuildState();
						} else {
							await this.persistGroups(items);
							if (!this.list.page.more && this.hasCompleteGroupSnapshot()) {
								await this.persistGroupListSnapshotMeta(this.groups, {
									total: page.total,
									rev: page.rev
								});
							}
						}
						cursor = this.list.page.cursor;
						more = this.list.page.more;
						fetchedPages += 1;
					}
					if (this.hasCompleteGroupSnapshot()) {
						await this.persistGroupSnapshot(this.groups);
					}
					return {
						items: mergedItems,
						more: this.list.page.more
					};
				} catch (error) {
					this.list.page.error = true;
					throw error;
				} finally {
					this.list.page.loading = false;
				}
			})();
			const trackedTask = task.finally(() => {
				this.list.tasks.bg = null;
			});
			this.list.tasks.bg = trackedTask;
			return trackedTask;
		},
		async ensureGroupListFullyLoaded(options = {}) {
				if (this.list.tasks.full) {
					return this.list.tasks.full;
				}
				const task = (async () => {
					if (this.list.tasks.load) {
						await this.list.tasks.load;
					}
					const pageLimit = Math.max(1, Math.min(
						GROUP_LIST_PAGE_LIMIT_MAX,
						toNumber(options.limit, GROUP_LIST_PAGE_LIMIT) || GROUP_LIST_PAGE_LIMIT
					));
					const maxPages = Math.max(1, Math.floor(
						toNumber(options.maxPages, GROUP_LIST_FULL_LOAD_PAGE_BUDGET) || GROUP_LIST_FULL_LOAD_PAGE_BUDGET
					));
					if (this.hasCompleteGroupSnapshot()) {
						await this.persistGroupSnapshot(this.groups);
						return this.groups;
					}
					if (this.list.sync.rev > 0 && this.list.page.total <= 0 && !this.list.page.more) {
						await this.pullGroupListPage({
							rev: this.list.sync.rev,
							cursor: '',
							limit: pageLimit
						});
					}
					if (this.hasCompleteGroupSnapshot()) {
						return this.groups;
					}
					const total = Math.max(0, Math.floor(Number(this.list.page.total || 0)));
						if (this.list.sync.rev > 0 && total > 0 && !this.list.page.more && this.groupSnapshotRowCount() < total) {
						await this.pullGroupListPage({
							rev: 0,
							cursor: '',
							limit: pageLimit
						});
					}
					let loadedPages = 0;
					while (this.list.page.more && this.list.page.cursor && loadedPages < maxPages) {
						await this.loadMoreGroupsFromCursor({
							...options,
							limit: pageLimit,
							maxPages: 1
						});
						loadedPages += 1;
					}
					if (this.hasCompleteGroupSnapshot()) {
						await this.persistGroupSnapshot(this.groups);
					}
					return this.groups;
				})();
				const trackedTask = task.finally(() => {
					if (this.list.tasks.full === trackedTask) {
						this.list.tasks.full = null;
					}
				});
				this.list.tasks.full = trackedTask;
				return trackedTask;
			},
		syncGroupMemberStats(group) {
			if (!group) {
				return;
			}
			const activeCount = this.countActiveMembers(group.members);
			const currentMemberCount = Number(group.memberCount);
			if (!group.memberPage.more || !Number.isFinite(currentMemberCount) || currentMemberCount < activeCount) {
				group.memberCount = activeCount;
			}
			group.onlineCount = this.countOnlineMembers(group.members);
		},
		applyGroupMembersSnapshot(group, members, options = {}) {
			if (!group) {
				return [];
			}
			this.ensureGroupState(group);
			const sourceMembers = Array.isArray(members) ? members : [];
			const normalizedMembers = sourceMembers
				.map(member => this.normalizeGroupMember({ ...member }))
				.filter(member => member && member.userId != null);
			group.members = normalizedMembers;
			setGroupMemberLookup(group, createMemberLookup(group.members));
			if (options.resetPaging !== false) {
				this.resetGroupMemberPagingState(group);
			}
			group.memberPage.loaded = options.loaded !== false;
			group.memberPage.error = false;
			group.memberPage.loadedAt = Date.now();
			this.syncGroupMemberStats(group);
			return group.members;
		},
		applyGroupMembersDelta(group, members, options = {}) {
			if (!group) {
				return [];
			}
			this.ensureGroupState(group);
			const nextMembers = (Array.isArray(members) ? members : [])
				.map(member => this.normalizeGroupMember({ ...member }))
				.filter(member => member && member.userId != null);
			if (!nextMembers.length) {
				this.syncGroupMemberStats(group);
				return [];
			}
			const mergedMembers = group.members.slice();
			let shouldReplaceMembers = false;
			const memberMap = new Map(mergedMembers.map(member => [String(member.userId), member]));
			nextMembers.forEach((incomingMember) => {
				const memberKey = String(incomingMember.userId);
				const oldMember = memberMap.get(memberKey);
				if (oldMember) {
					Object.assign(oldMember, incomingMember);
					this.normalizeGroupMember(oldMember);
					upsertGroupMemberLookup(group, oldMember);
				} else {
					mergedMembers.push(incomingMember);
					shouldReplaceMembers = true;
					memberMap.set(memberKey, incomingMember);
					upsertGroupMemberLookup(group, incomingMember);
				}
			});
			if (shouldReplaceMembers) {
				Vue.set(group, 'members', mergedMembers);
			}
			group.memberPage.error = false;
			group.memberPage.loadedAt = Date.now();
			this.syncGroupMemberStats(group);
			return nextMembers;
		},
		async persistGroupMembers(group, options = {}) {
			if (!group || !group.id || !Array.isArray(group.members)) {
				return;
			}
			if (!group.memberPage.loaded) {
				return;
			}
			const db = getDB();
			try {
				const incomingMembers = Array.isArray(options.incomingMembers) ? options.incomingMembers : null;
				if (options.incrementalOnly === true) {
					if (!incomingMembers || incomingMembers.length === 0) {
						return;
					}
					const tombstoneUserIds = [];
					const persistMembers = [];
					incomingMembers.forEach((member) => {
						if (!member || member.userId === undefined || member.userId === null) {
							return;
						}
						if (member.quit === true) {
							tombstoneUserIds.push(member.userId);
							return;
						}
						persistMembers.push(member);
					});
					if (tombstoneUserIds.length > 0) {
						await db.removeGroupMembersByUserIds(group.id, tombstoneUserIds);
					}
					if (persistMembers.length > 0) {
						await db.saveGroupMembers(group.id, persistMembers);
					}
					return;
				}
				if (!group.memberPage.more) {
					// 全量已完成：以内存合并状态为权威源做整表协调，确保 quit=true 不再持久化
					const liveMembers = group.members.filter((member) => member && member.quit !== true);
					await db.syncGroupMembers(group.id, liveMembers);
					await db.saveConfig(groupMembersSnapshotConfigKey(group.id), {
						complete: true,
						total: liveMembers.length,
						snapshotAt: Date.now()
					});
					return;
				}
				// 分页未拉完：用本页 incomingMembers 做增量落盘 + tombstone 删除
				if (!incomingMembers || incomingMembers.length === 0) {
					return;
				}
				const tombstoneUserIds = [];
				const persistMembers = [];
				incomingMembers.forEach((member) => {
					if (!member || member.userId === undefined || member.userId === null) {
						return;
					}
					if (member.quit === true) {
						tombstoneUserIds.push(member.userId);
						return;
					}
					persistMembers.push(member);
				});
				if (tombstoneUserIds.length > 0) {
					await db.removeGroupMembersByUserIds(group.id, tombstoneUserIds);
				}
				if (persistMembers.length > 0) {
					await db.saveGroupMembers(group.id, persistMembers);
				}
				await db.deleteConfig(groupMembersSnapshotConfigKey(group.id));
			} catch (error) {
				console.warn('保存群成员本地快照失败', error);
			}
		},
		async restoreGroupMembersFromLocal(group, options = {}) {
			if (!group || !group.id || group.memberPage.loaded || group.memberPage.loading) {
				return [];
			}
			try {
				const db = getDB();
				const snapshotMeta = await db.findConfig(groupMembersSnapshotConfigKey(group.id));
				if (!snapshotMeta || snapshotMeta.complete !== true) {
					return [];
				}
				const members = await db.findGroupMembers(group.id);
				const validMembers = (Array.isArray(members) ? members : []).filter((member) => {
					const snapshotAt = toNumber(member && member.snapshotAt);
					return !snapshotAt || Date.now() - snapshotAt <= GROUP_MEMBER_CACHE_TTL_MS;
				});
				if (!validMembers.length) {
					return [];
				}
				const expectedTotal = Math.max(
					0,
					Math.floor(toNumber(snapshotMeta.total, 0)),
					Math.floor(toNumber(group.memberCount, 0))
				);
				if (expectedTotal > 0 && validMembers.length < expectedTotal) {
					await db.deleteConfig(groupMembersSnapshotConfigKey(group.id));
					return [];
				}
				this.applyGroupMembersSnapshot(group, validMembers, {
					loaded: options.loaded !== false
				});
				if (options.refreshOnline === true) {
					this.refreshMemberOnline(group.id, {
						deferApply: true
					}).catch(() => []);
				}
				return group.members;
			} catch (error) {
				console.warn('恢复群成员本地快照失败', error);
				return [];
			}
		},
		clearRemovedMemberMessages(id, content) {
			const group = this.findGroup(id);
			if (!group || !Array.isArray(group.topMessages)) return;
			let payload;
			try { payload = JSON.parse(content); } catch (_) { return; }
			const cutoffs = mergeMessageRemovalCutoffs({}, payload);
			const retained = group.topMessages.filter(message => !isRemovedMemberMessage(message, cutoffs));
			retained.forEach(message => applyGroupMessageRemoval(message, cutoffs));
			group.topMessages.splice(0, group.topMessages.length, ...retained);
			this.schedulePersistGroup(group);
		},
		updateTopMessage(id, latestTopMessage) {
			let group = this.findGroup(id);
			if (!group) {
				return;
			}
			if (!Array.isArray(group.topMessages)) {
				group.topMessages = [];
			}
			if (!latestTopMessage) {
				group.topMessages = [];
				return;
			}
			const nextTopMessages = removeTopMessages(group.topMessages, [latestTopMessage.id]);
			// 最新置顶消息始终放在尾部，确保头部横幅在刷新兜底阶段优先展示最新一条。
			nextTopMessages.push(latestTopMessage);
			group.topMessages.splice(0, group.topMessages.length, ...normalizeTopMessages(nextTopMessages));
		},
		removeTopMessage(id, messageId) {
			let group = this.findGroup(id);
			if (!group || messageId === undefined || messageId === null || messageId === '') {
				return;
			}
			if (!Array.isArray(group.topMessages)) {
				group.topMessages = [];
			}
			const nextTopMessages = removeTopMessages(group.topMessages, [messageId]);
			group.topMessages.splice(0, group.topMessages.length, ...nextTopMessages);
		},
		setMuted(id, isMuted) {
			let group = this.findGroup(id);
			if (!group) {
				return;
			}
			group.isMuted = isMuted;
		},
		setAllMuted(id, isAllMuted) {
			let group = this.findGroup(id);
			if (!group) {
				return;
			}
			group.isAllMuted = isAllMuted;
		},
		setGroupMembersMuted(id, userIds, isMuted) {
			let group = this.findGroup(id);
			if (!group || !Array.isArray(userIds)) {
				return;
			}
			this.ensureGroupState(group);
			userIds.forEach((userId) => {
				const member = this.findGroupMember(id, userId);
				if (member) {
					member.isMuted = Boolean(isMuted);
				}
			});
		},
		setRtcInfo(id, rtcInfo) {
			let group = this.findGroup(id);
			if (!group) {
				return;
			}
			group.rtcInfo = rtcInfo;
		},
		updateGroupPermissions(groupId, permissions) {
			// 更新群组权限
			let group = this.findGroup(groupId);
			if (group) {
				if (permissions.isAllowViewMemberProfile !== undefined) {
					group.isAllowViewMemberProfile = permissions.isAllowViewMemberProfile;
				}
				if (permissions.isAllowViewOnlineCount !== undefined) {
					group.isAllowViewOnlineCount = permissions.isAllowViewOnlineCount;
				}
				if (permissions.isAllowViewHistory !== undefined) {
					group.isAllowViewHistory = permissions.isAllowViewHistory;
				}
			}
		},
		refreshMember(id, options = {}) {
				let group = this.findGroup(id);
				if (!group) {
					return Promise.resolve([]);
			}
			this.ensureGroupState(group);
			if (group.memberTasks.refresh) {
				return group.memberTasks.refresh;
			}
			const refreshTask = (async () => {
				let forceRefresh = options.forceRefresh === true;
				let expectedCount = this.resolveExpectedMemberCount(group, options);
				let currentCount = this.countActiveMembers(group.members);
				let shouldResetSnapshot = options.forceFull === true
					|| !group.memberPage.loaded
					|| group.members.length === 0
					|| group.memberPage.error === true
					|| (expectedCount > 0 && currentCount > expectedCount)
					|| (expectedCount > 0 && currentCount < expectedCount && !group.memberPage.more);

				if (options.restoreLocal !== false && shouldResetSnapshot && !group.memberPage.loaded && group.members.length === 0) {
					const localMembers = await this.restoreGroupMembersFromLocal(group, {
						refreshOnline: false
					});
					if (Array.isArray(localMembers) && localMembers.length > 0 && options.localOnly === true) {
						return localMembers;
					}
					expectedCount = this.resolveExpectedMemberCount(group, options);
					currentCount = this.countActiveMembers(group.members);
					shouldResetSnapshot = options.forceFull === true
						|| !group.memberPage.loaded
						|| group.members.length === 0
						|| group.memberPage.error === true
						|| (expectedCount > 0 && currentCount > expectedCount);
					if (expectedCount > 0 && currentCount < expectedCount && !group.memberPage.more) {
						shouldResetSnapshot = true;
					}
					forceRefresh = options.forceRefresh === true
						|| !this.hasCompleteMemberSnapshot(id);
				}

				const canUseCache = !forceRefresh
					&& group.memberPage.loaded
					&& !group.memberPage.loading
					&& (!shouldResetSnapshot || (expectedCount > 0 && currentCount >= expectedCount));
				if (canUseCache) {
					if (options.refreshOnline === true) {
						await this.refreshMemberOnline(id, {
							deferApply: options.deferApply,
							chunkSize: options.chunkSize
						}).catch(() => []);
					}
					return group.members;
				}

				const fullSnapshot = shouldResetSnapshot;
				const requestRev = fullSnapshot ? 0 : group.memberSync.rev;
				const pageLimit = Math.max(1, Math.min(
					GROUP_MEMBER_PAGE_LIMIT_MAX,
					toNumber(options.pageLimit, fullSnapshot ? GROUP_MEMBER_FIRST_PAGE_LIMIT : GROUP_MEMBER_PAGE_LIMIT) ||
						(fullSnapshot ? GROUP_MEMBER_FIRST_PAGE_LIMIT : GROUP_MEMBER_PAGE_LIMIT)
				));
				group.memberPage.loading = true;
				group.memberPage.error = false;
				group.memberTasks.mode = fullSnapshot ? 'full' : 'delta';
				try {
					const page = await this.fetchGroupMemberSyncPage(id, requestRev, {
						limit: pageLimit,
						maxPages: 1
					});
					const nextMembers = Array.isArray(page.items) ? page.items : [];
					this.setGroupMemberPageState(group, page, page.requestRev);
					const shouldDefer = options.deferApply !== false &&
						(options.deferApply
							|| nextMembers.length > MEMBER_DEFER_THRESHOLD
							|| group.members.length > MEMBER_DEFER_THRESHOLD);
					if (fullSnapshot) {
						const nextMemberLookup = Object.create(null);
						const normalizeChunk = (chunk) => {
							chunk.forEach(m1 => {
								this.normalizeGroupMember(m1);
								if (m1 && m1.userId != null) {
									nextMemberLookup[String(m1.userId)] = m1;
								}
							});
						};
						if (shouldDefer) {
							await applyInIdleChunks(nextMembers, normalizeChunk, options.chunkSize || MEMBER_APPLY_CHUNK_SIZE);
						} else {
							normalizeChunk(nextMembers);
						}
						group.members = nextMembers;
						setGroupMemberLookup(group, nextMemberLookup);
						group.memberPage.loaded = true;
						group.memberPage.error = false;
						group.memberPage.loadedAt = Date.now();
					} else {
						this.applyGroupMembersDelta(group, nextMembers);
						group.memberPage.loaded = true;
					}
					this.syncGroupMemberStats(group);
					await this.persistGroupMembers(group, {
						incomingMembers: nextMembers
					});
					const needOnlineRefresh = options.refreshOnline === true
						|| (!fullSnapshot && options.refreshOnline !== false);
					if (needOnlineRefresh) {
						await this.refreshMemberOnline(id, {
							deferApply: options.deferApply !== false,
							chunkSize: options.chunkSize
						}).catch(() => []);
					}
					return group.members;
				} catch (error) {
					group.memberPage.error = true;
					throw error;
				} finally {
					group.memberPage.loading = false;
					group.memberTasks.mode = '';
				}
			})();
			group.memberTasks.refresh = refreshTask.finally(() => {
				group.memberTasks.refresh = null;
			});
			return group.memberTasks.refresh;
		},
		loadMoreMembersFromCursor(id, options = {}) {
			const group = this.findGroup(id);
			if (!group) {
				return Promise.resolve({
					items: [],
					more: false
				});
			}
			this.ensureGroupState(group);
			if (group.memberTasks.more) {
				return group.memberTasks.more;
			}
			if (group.memberPage.loading || group.memberPage.loadingMore || !group.memberPage.more || !group.memberPage.cursor) {
				return Promise.resolve({
					items: [],
					more: group.memberPage.more
				});
			}
			const loadTask = (async () => {
				group.memberPage.loadingMore = true;
				group.memberPage.moreError = false;
				const pageLimit = Math.max(1, Math.min(
					GROUP_MEMBER_PAGE_LIMIT_MAX,
					toNumber(options.limit, GROUP_MEMBER_PAGE_LIMIT) || GROUP_MEMBER_PAGE_LIMIT
				));
				const pageBudget = Math.max(1, Math.floor(toNumber(options.maxPages, 1) || 1));
				let cursor = group.memberPage.cursor;
				let more = group.memberPage.more;
				let mergedItems = [];
				let fetchedPages = 0;
				try {
					while (more && cursor && fetchedPages < pageBudget) {
						const page = await this.fetchGroupMemberSyncPage(id, group.memberSync.base, {
							cursor,
							limit: pageLimit,
							maxPages: 1
						});
						const nextMembers = Array.isArray(page.items) ? page.items : [];
						this.applyGroupMembersDelta(group, nextMembers);
						group.memberPage.loaded = true;
						group.memberPage.loadedAt = Date.now();
						this.setGroupMemberPageState(group, page, page.requestRev);
						cursor = group.memberPage.cursor;
						more = group.memberPage.more;
						if (nextMembers.length > 0) {
							mergedItems = mergedItems.concat(nextMembers);
						}
						this.syncGroupMemberStats(group);
						await this.persistGroupMembers(group, {
							incomingMembers: nextMembers
						});
						fetchedPages += 1;
						if (options.refreshOnline === true) {
							await this.refreshMemberOnline(id, {
								deferApply: true,
								chunkSize: options.chunkSize
							}).catch(() => []);
						}
					}
					return {
						items: mergedItems,
						more: group.memberPage.more
					};
				} catch (error) {
					group.memberPage.moreError = true;
					throw error;
				} finally {
					group.memberPage.loadingMore = false;
				}
			})();
			group.memberTasks.more = loadTask.finally(() => {
				group.memberTasks.more = null;
			});
			return group.memberTasks.more;
		},
			async ensureGroupMembersFullyLoaded(id, options = {}) {
				const group = this.findGroup(id);
				if (!group) {
					return [];
				}
				this.ensureGroupState(group);
				if (this.hasCompleteMemberSnapshot(id)) {
					return Array.isArray(group.members) ? group.members : [];
				}
				const pageLimit = Math.max(1, Math.min(
					GROUP_MEMBER_PAGE_LIMIT_MAX,
					Math.max(
						toNumber(options.pageLimit, GROUP_MEMBER_BULK_PAGE_LIMIT) || GROUP_MEMBER_BULK_PAGE_LIMIT,
						GROUP_MEMBER_BULK_PAGE_LIMIT
					)
				));
				await this.refreshMember(id, {
					...options,
					forceFull: options.forceFull === true || !group.memberPage.loaded,
					pageLimit
				});
				const maxPages = Math.max(1, Math.floor(
					toNumber(options.maxPages, GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET) || GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET
				));
				let loadedPages = 0;
				while (group.memberPage.more && group.memberPage.cursor && loadedPages < maxPages) {
					await this.loadMoreMembersFromCursor(id, {
						...options,
						limit: pageLimit,
						maxPages: 1
					});
					loadedPages += 1;
				}
				return Array.isArray(group.members) ? group.members : [];
			},
		refreshMemberOnline(id, options = {}) {
			let group = this.findGroup(id);
			if (!group || !Array.isArray(group.members)) {
				return Promise.resolve([]);
			}
			return http({
				url: `/group/members/online/${id}`,
				method: 'get'
			}).then(userIds => {
				const onlineUserIds = Array.isArray(userIds) ? userIds : [];
				const onlineUserIdSet = new Set(onlineUserIds.map(userId => String(userId)));
				const userStore = useUserStore();
				const selfId = userStore.userInfo?.id;
				group.onlineCount = onlineUserIds.length;
				const shouldDefer = options.deferApply !== false &&
					(options.deferApply || group.members.length > MEMBER_DEFER_THRESHOLD);
				const applyOnline = (members) => {
					members.forEach(m => {
						// 更新成员在线状态
						const canShowOnline = m.isOnlineVisible !== false || m.userId === selfId;
						m.online = canShowOnline ? onlineUserIdSet.has(String(m.userId)) : null;
					});
				};
				const applyTask = shouldDefer
					? applyInIdleChunks(group.members, applyOnline, options.chunkSize || MEMBER_APPLY_CHUNK_SIZE)
					: Promise.resolve().then(() => applyOnline(group.members));
				return applyTask.then(() => {
					this.refreshMmeberSort(id);
					this.syncGroupMemberStats(group);
					return onlineUserIds;
				});
			})
		},
		refreshMmeberSort(id) {
			let group = this.findGroup(id);
			group.members.sort((m1, m2) => {
				// 在线的放前面
				if (m1.online && !m2.online) {
					return -1
				}
				if (!m1.online && m2.online) {
					return 1
				}
				// 群主在前面
				if (m1.userId == group.ownerId) {
					return -1;
				}
				if (m2.userId == group.ownerId) {
					return 1;
				}
				// 管理员在前面
				if (m1.isManager && !m2.isManager) {
					return -1;
				}
				if (!m1.isManager && m2.isManager) {
					return 1;
				}
				return 0;
			})
		},
		clear() {
			this.groupInfoRequestEpoch++;
			this.groupInfoTasks.clear();
			this.groups = [];
			this.groupMap.clear();
			this.groupPersistTimerMap.forEach((timer) => clearTimeout(timer));
			this.groupPersistTimerMap.clear();
			resetMemberGroupIndex();
			this.resetGroupListState();
		},
		getMemberLookup(id, memberCount = 0, memberVersion = 0) {
			// memberCount/memberVersion 作为调用方的响应式刷新标记，查找表本身保持非枚举运行期索引。
			void memberCount;
			void memberVersion;
			const group = this.findGroup(id);
			return ensureGroupMemberLookup(group);
		},
		getMemberPageState(id) {
			const group = this.findGroup(id);
			if (!group) {
				return {
					more: false,
					cursor: '',
					total: 0,
					loading: false,
					loadError: false,
					rev: 0
				};
			}
			this.ensureGroupState(group);
			return {
				more: Boolean(group.memberPage.more && group.memberPage.cursor),
				cursor: group.memberPage.cursor ? String(group.memberPage.cursor) : '',
				total: Math.max(0, Math.floor(toNumber(group.memberPage.total, group.memberCount))),
				loading: Boolean(group.memberPage.loading || group.memberPage.loadingMore),
				loadError: Boolean(group.memberPage.error || group.memberPage.moreError),
				rev: toIntVersion(group.memberSync.rev)
			};
		},
		findGroupMember(groupId, userId) {
			if (userId == null) {
				return null;
			}
			return this.getMemberLookup(groupId)[String(userId)] || null;
		},
		loadGroup() {
			if (this.list.tasks.load) {
				return this.list.tasks.load;
			}
			let resolveLocalReady = null;
			let rejectLocalReady = null;
				const localReadyPromise = new Promise((resolve, reject) => {
				resolveLocalReady = resolve;
				rejectLocalReady = reject;
			});
			this.list.tasks.localReady = localReadyPromise;
				const task = (async () => {
					let localGroups;
					let snapshotMeta;
					try {
						localGroups = await getDB().findAllGroups();
						snapshotMeta = await this.findGroupListSnapshotMeta();
						if (Array.isArray(localGroups) && localGroups.length > 0 && this.groups.length === 0) {
							this.initGroups(localGroups);
						}
						this.resetGroupListState({
							clearTasks: false
						});
						if (snapshotMeta && !this.isGroupListSnapshotMetaUsable(snapshotMeta, localGroups)) {
							await this.deleteGroupListSnapshotMeta();
							snapshotMeta = null;
						}
						this.list.sync.rev = this.buildGroupListRequestVersion(localGroups, snapshotMeta);
						this.list.sync.base = this.list.sync.rev;
						resolveLocalReady && resolveLocalReady(this.groups);
					} catch (error) {
					rejectLocalReady && rejectLocalReady(error);
					throw error;
				}
				let requestRev = this.list.sync.rev;
				this.list.page.loading = true;
				this.list.page.error = false;
				try {
					await this.pullGroupListPage({
						rev: requestRev,
						cursor: '',
						limit: GROUP_LIST_FIRST_PAGE_LIMIT
					});
					if (requestRev > 0 && this.hasIncompleteGroupSnapshot()) {
						await this.pullGroupListPage({
							rev: 0,
							cursor: '',
							limit: GROUP_LIST_FIRST_PAGE_LIMIT
						});
					}
					requestRev = this.list.sync.base;
				} catch (error) {
					this.list.page.error = true;
					this.list.page.loading = false;
					throw error;
				}
				this.list.page.loading = false;
				return this.groups;
			})();
			const trackedTask = task.finally(() => {
				if (this.list.tasks.load === trackedTask) {
					this.list.tasks.load = null;
				}
				if (this.list.tasks.localReady === localReadyPromise) {
					this.list.tasks.localReady = null;
				}
			});
			this.list.tasks.load = trackedTask;
			return trackedTask;
		},
		loadGroupLocalReady() {
			return this.list.tasks.localReady || Promise.resolve();
		},
		refreshGroupFromRemote() {
			return this.pullGroupListPage({
				rev: this.list.sync.rev,
				cursor: '',
				limit: GROUP_LIST_PAGE_LIMIT
			}).then(() => this.groups);
		}
	},
	getters: {
		findGroup: (state) => (id) => {
			const groupKey = resolveGroupStoreKey(id);
			if (!groupKey) {
				return null;
			}
			const group = state.groupMap.get(groupKey);
			if (group) {
				return group;
			}
			const groups = Array.isArray(state.groups) ? state.groups : [];
			const matchedGroup = groups.find((item) => resolveGroupStoreKey(item && item.id) === groupKey) || null;
			if (matchedGroup) {
				state.groupMap.set(groupKey, matchedGroup);
			}
			return matchedGroup;
		},
		isMembersLoading: (state) => (id) => {
			const group = state.findGroup(id);
			return Boolean(group && group.memberPage.loading);
		},
		isMembersLoaded: (state) => (id) => {
			const group = state.findGroup(id);
			return Boolean(group && group.memberPage.loaded);
		},
		isMembersLoadError: (state) => (id) => {
			const group = state.findGroup(id);
			return Boolean(group && group.memberPage.error);
		},
		isGroup: (state) => (id) => {
			let group = state.findGroup(id);
			return group && !group.quit
		},
		isManager: (state) => (groupId, userId) => {
			// 判断用户是否为群管理员
			let group = state.findGroup(groupId);
			if (!group) return false;
			if (group.ownerId == userId) {
				return true;
			}
			let member = ensureGroupMemberLookup(group)[String(userId)];
			return Boolean(member && member.isManager === true);
		},
	}
});
